import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTvPairing } from "@/context/TvPairingContext";
import { useAudioPlayer } from "@/context/AudioContext";

type FocusKey = 'play' | 'prev' | 'next' | 'volup' | 'voldown' | 'mute' | 'home' | 'player' | 'back' | 'disconnect';

export default function TvRemotePage() {
  const navigate = useNavigate();
  const { session, remote, disconnectDevice } = useTvPairing();
  const { currentStation, isPlaying, volume } = useAudioPlayer();
  const [focused, setFocused] = useState<FocusKey>('play');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [localVol, setLocalVol] = useState(volume);

  useEffect(() => { setLocalVol(volume); }, [volume]);

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 1500);
  };

  // ── Keyboard grid navigation ──
  // Layout: 3 rows:
  // Row 0: [prev]  [play]  [next]
  // Row 1: [volup] [mute]  [voldown]
  // Row 2: [home]  [player] [back]
  // Row 3: [disconnect] ...
  const grid: FocusKey[][] = [
    ['prev', 'play', 'next'],
    ['volup', 'mute', 'voldown'],
    ['home', 'player', 'back'],
    ['disconnect'],
  ];

  const findPos = (key: FocusKey): [number, number] => {
    for (let r = 0; r < grid.length; r++) {
      const c = grid[r].indexOf(key);
      if (c !== -1) return [r, c];
    }
    return [0, 1];
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const [row, col] = findPos(focused);

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newRow = (row - 1 + grid.length) % grid.length;
        const newCol = Math.min(col, grid[newRow].length - 1);
        setFocused(grid[newRow][newCol]);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newRow = (row + 1) % grid.length;
        const newCol = Math.min(col, grid[newRow].length - 1);
        setFocused(grid[newRow][newCol]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const newCol = (col - 1 + grid[row].length) % grid[row].length;
        setFocused(grid[row][newCol]);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const newCol = (col + 1) % grid[row].length;
        setFocused(grid[row][newCol]);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        executeAction(focused);
      } else if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault();
        navigate('/tv-app');
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [focused, navigate]);

  const executeAction = (key: FocusKey) => {
    switch (key) {
      case 'play':
        remote.togglePlay();
        showFeedback(isPlaying ? '⏸ Pausado' : '▶ Reproduciendo');
        break;
      case 'prev':
        remote.prevStation();
        showFeedback('⏮ Estación anterior');
        break;
      case 'next':
        remote.nextStation();
        showFeedback('⏭ Estación siguiente');
        break;
      case 'volup':
        remote.volumeUp();
        showFeedback('🔊 Volumen +');
        break;
      case 'voldown':
        remote.volumeDown();
        showFeedback('🔉 Volumen −');
        break;
      case 'mute':
        remote.toggleMute();
        showFeedback('🔇 Mute');
        break;
      case 'home':
        navigate('/tv-app');
        break;
      case 'player':
        if (currentStation) {
          navigate(`/tv-app/player/${currentStation.id}`);
        } else {
          showFeedback('No hay estación reproduciendo');
        }
        break;
      case 'back':
        navigate('/tv-app');
        break;
      case 'disconnect':
        disconnectDevice();
        navigate('/tv-app/qr');
        break;
    }
  };

  const btnClass = (key: FocusKey, extra = '') =>
    `flex flex-col items-center justify-center gap-1.5 rounded-2xl transition-all cursor-pointer outline-none whitespace-nowrap select-none ${extra} ${
      focused === key
        ? 'bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105'
        : 'bg-white/10 hover:bg-white/15 text-white'
    }`;

  // Redirect if not connected
  if (session.state !== 'CONNECTED_MOCK') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col items-center justify-center gap-6 px-8">
        <div className="w-20 h-20 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <i className="ri-link-unlink text-amber-400 text-3xl"></i>
        </div>
        <h1 className="text-2xl font-bold text-white">Dispositivo no vinculado</h1>
        <p className="text-base text-white/60 text-center max-w-md">Vinculá tu dispositivo desde la pantalla de QR para usar el control remoto.</p>
        <button
          onClick={() => navigate('/tv-app/qr')}
          className="flex items-center gap-2 px-6 py-4 rounded-2xl bg-accent-400 text-[#0a0a0f] font-bold text-base cursor-pointer whitespace-nowrap transition-all hover:bg-accent-300 outline-none focus:ring-4 focus:ring-accent-400/30"
        >
          <i className="ri-qr-code-line text-lg"></i>
          Ir a Vincular TV
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none">
      {/* Ambient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-accent-400/5"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.03)_0%,_transparent_70%)]"></div>

      {/* Demo banner */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-amber-500/10 border-b border-amber-500/20 px-6 py-2 flex items-center justify-center gap-2">
        <i className="ri-flask-line text-amber-400 text-sm"></i>
        <span className="text-xs text-amber-400 font-semibold">MODO DEMOSTRACIÓN — El control remoto opera dentro de la misma sesión de prueba</span>
      </div>

      <div className="relative z-10 h-screen flex flex-col items-center justify-center px-4">
        {/* Header */}
        <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-8 py-4">
          <button
            onClick={() => navigate('/tv-app')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-white"
          >
            <i className="ri-arrow-left-line"></i>
            Volver
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/40">{session.deviceName}</span>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              <span className="text-xs text-emerald-400 font-semibold">CONECTADO</span>
            </div>
          </div>
        </div>

        {/* Now Playing indicator */}
        {currentStation && (
          <div className="absolute top-20 left-0 right-0 flex justify-center">
            <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <img src={currentStation.logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
              <div>
                <p className="text-sm font-bold text-white truncate max-w-[200px]">{currentStation.name}</p>
                <p className="text-[10px] text-white/50">{currentStation.frequency} · {isPlaying ? 'Sonando' : 'Pausado'}</p>
              </div>
              {isPlaying && (
                <div className="flex items-end gap-0.5 h-3">
                  {[2, 4, 3, 5, 2].map((h, i) => (
                    <div key={i} className="w-1 rounded-full bg-emerald-400" style={{ height: `${h * 2}px`, animation: `tv-eq 0.5s ease-in-out infinite`, animationDelay: `${i * 0.1}s` }}></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Remote Control Grid */}
        <div className="flex flex-col items-center gap-4">
          {/* Row 0: Prev / Play / Next */}
          <div className="flex items-center gap-4">
            <button
              tabIndex={0}
              onClick={() => executeAction('prev')}
              className={btnClass('prev', 'w-20 h-20')}
            >
              <i className="ri-skip-back-fill text-2xl"></i>
              <span className="text-[10px] font-semibold">Anterior</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('play')}
              className={btnClass('play', 'w-24 h-24 text-xl')}
            >
              <i className={`text-3xl ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
              <span className="text-[10px] font-semibold">{isPlaying ? 'Pausa' : 'Play'}</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('next')}
              className={btnClass('next', 'w-20 h-20')}
            >
              <i className="ri-skip-forward-fill text-2xl"></i>
              <span className="text-[10px] font-semibold">Siguiente</span>
            </button>
          </div>

          {/* Row 1: Vol+ / Mute / Vol- */}
          <div className="flex items-center gap-4">
            <button
              tabIndex={0}
              onClick={() => executeAction('volup')}
              className={btnClass('volup', 'w-20 h-20')}
            >
              <i className="ri-volume-up-fill text-2xl"></i>
              <span className="text-[10px] font-semibold">Vol +</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('mute')}
              className={btnClass('mute', 'w-24 h-20')}
            >
              <i className={`text-2xl ${localVol === 0 ? 'ri-volume-mute-fill' : 'ri-volume-up-fill'}`}></i>
              <span className="text-[10px] font-semibold">{localVol === 0 ? 'Unmute' : 'Mute'}</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('voldown')}
              className={btnClass('voldown', 'w-20 h-20')}
            >
              <i className="ri-volume-down-fill text-2xl"></i>
              <span className="text-[10px] font-semibold">Vol −</span>
            </button>
          </div>

          {/* Volume bar */}
          <div className="w-64 h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-400 transition-all duration-200"
              style={{ width: `${localVol}%` }}
            ></div>
          </div>

          {/* Row 2: Home / Player / Back */}
          <div className="flex items-center gap-4 mt-2">
            <button
              tabIndex={0}
              onClick={() => executeAction('home')}
              className={btnClass('home', 'w-20 h-16')}
            >
              <i className="ri-home-5-line text-xl"></i>
              <span className="text-[10px] font-semibold">Inicio</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('player')}
              className={btnClass('player', 'w-24 h-16')}
            >
              <i className="ri-fullscreen-line text-xl"></i>
              <span className="text-[10px] font-semibold">Player</span>
            </button>

            <button
              tabIndex={0}
              onClick={() => executeAction('back')}
              className={btnClass('back', 'w-20 h-16')}
            >
              <i className="ri-arrow-go-back-line text-xl"></i>
              <span className="text-[10px] font-semibold">Volver</span>
            </button>
          </div>

          {/* Row 3: Disconnect */}
          <button
            tabIndex={0}
            onClick={() => executeAction('disconnect')}
            className={btnClass('disconnect', 'w-48 h-12 mt-4 text-sm')}
          >
            <i className="ri-link text-base"></i>
            Desvincular Dispositivo
          </button>
        </div>

        {/* Feedback toast */}
        {feedback && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/10 text-white text-sm font-bold animate-pulse">
            {feedback}
          </div>
        )}

        {/* Keyboard hint */}
        <div className="absolute bottom-4 left-4 text-[10px] text-white/20">
          Usá las flechas del teclado y Enter para navegar
        </div>
      </div>

      <style>{`
        @keyframes tv-eq {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}